// 客户端与服务端共用：命令、私有消息、座位旋转和手牌遮挡。
// 服务端权威：对局只在 GuandanTable 推进，客户端只提交想出的牌 id。
// 发给每位玩家的对局是“遮挡 + 旋转”过的副本：别人的手牌换成同样张数的空白牌、去掉发牌种子（否则能算出全部手牌），
// 再把座位转到“自己 = 0 号座”，这样单机的牌桌界面不用改就能直接用。
import type { Card } from './cards.ts';
import type { Match, Seat } from './match.ts';
import type { Play, PlayType } from './rules.ts';

/** 房间里的一个座位；发送给客户端前已旋转为接收者的 0–3 视角。 */
export interface SeatInfo { name: string; char: string; human: boolean; online: boolean; host: boolean; title?: string; ready?: boolean } // title：江湖名号（作者 11-03）

export type TableCommand = (
  | { t: 'ready'; ready: boolean }
  | { t: 'start' }
  | { t: 'play'; ids: number[]; as?: { type: PlayType; key: number } }
  | { t: 'next' }
  | { t: 'return'; id: number }
  | { t: 'retrySettle' }
  | { t: 'leave' }
) & { commandId?: string; expectedVersion?: number };

export type ClientCommand = TableCommand & { commandId: string; expectedVersion: number };

export type ServerMsg =
  /** 房间信息：seats 已按“你 = 0”旋转；phase=lobby 时在等人。 */
  | { t: 'room'; code: string; seats: (SeatInfo | null)[]; phase: Phase; you: string; limit: number; stateVersion?: number; matchId?: string; deadlineAt?: number }
  /** 整局状态（开局、新一副、重连时）。 */
  | { t: 'state'; m: Match; stateVersion?: number }
  /** 某家出了一手（出的牌本来就公开）：seat 已旋转；m 是出完之后的状态。 */
  | { t: 'act'; seat: Seat; play: Play | null; m: Match; stateVersion?: number; deadlineAt?: number }
  /** 轮到你还牌：可还的牌。 */
  | { t: 'askReturn'; options: Card[]; from: Seat; card: Card; stateVersion?: number; deadlineAt?: number }
  /** 一副结算后还在等谁点“下一副”（已旋转的座位）。 */
  | { t: 'waiting'; seats: Seat[]; stateVersion?: number }
  | { t: 'ack'; commandId: string; ok: boolean; code: string; msg?: string; stateVersion: number }
  | { t: 'error'; msg: string; code?: string; stateVersion?: number };

export type Phase = 'lobby' | 'play' | 'result' | 'return' | 'settling' | 'final';

/** 绝对座位 → 以 viewer 为 0 号的座位。 */
export const rot = (s: number, viewer: number): Seat => (((s - viewer) % 4) + 4) % 4 as Seat;
/** 旋转后的座位 → 绝对座位。 */
export const unrot = (s: number, viewer: number): Seat => ((s + viewer) % 4) as Seat;

/** 把整局转到 viewer 的视角：座位换算；viewer 是奇数座时两队对调（我方永远是 0 队）。 */
export function rotateMatch(m: Match, viewer: number): Match {
  const k = viewer % 4;
  const s = (x: Seat): Seat => rot(x, k);
  const t = (x: 0 | 1): 0 | 1 => (k % 2 ? (1 - x) as 0 | 1 : x);
  const pair = <T,>(p: [T, T]): [T, T] => (k % 2 ? [p[1], p[0]] : [p[0], p[1]]);
  const r = m.round;
  const hands: Card[][] = [0, 1, 2, 3].map((i) => r.hands[unrot(i, k)]);
  return {
    ...m,
    teamLevel: pair(m.teamLevel),
    aFails: pair(m.aFails),
    winner: m.winner === null ? null : t(m.winner),
    results: m.results.map((x) => ({ ...x, order: x.order.map(s), winnerTeam: t(x.winnerTeam) })),
    round: {
      ...r,
      levelTeam: t(r.levelTeam),
      hands,
      turn: s(r.turn),
      lead: s(r.lead),
      last: r.last && { ...r.last, seat: s(r.last.seat) },
      finished: r.finished.map(s),
      tributes: r.tributes.map((x) => ({ ...x, from: s(x.from), to: s(x.to) })),
      history: r.history.map((x) => ({ ...x, seat: s(x.seat) })),
    },
  };
}

/** 遮挡：只留 viewer 自己的手牌，别人的换成同样张数的空白牌（id 为负数），并去掉发牌种子。 */
export function maskMatch(m: Match, viewer: number): Match {
  const r = m.round;
  return {
    ...m,
    seed: 0,
    round: { ...r, hands: r.hands.map((h, i) => (i === viewer ? h.map((c) => ({ ...c })) : h.map((_, j) => ({ id: -1 - j, suit: 'S', rank: 0 } as Card)))) },
  };
}

/** 发给某位玩家的对局 = 先遮挡、再旋转。 */
export const viewFor = (m: Match, viewer: number): Match => rotateMatch(maskMatch(m, viewer), viewer);

/** 房号：4 位数字。 */
export const isRoomCode = (s: string): boolean => /^\d{4}$/.test(s);
